import api from '@/lib/api'

interface LoginRequest {
  email: string
  password: string
}

interface LoginResponse {
  /** Short-lived access token (alias for `accessToken`, preserved for back-compat). */
  token: string
  accessToken?: string
  /** Long-lived refresh token. Persist alongside the access token. */
  refreshToken?: string
  user: {
    id: string
    name: string
    email: string
    role: string
    phone?: string
    photo_url?: string
    approved: boolean
    /** Permission strings (e.g. 'tournaments.create', '*'). */
    permissions?: string[]
  }
}

interface User {
  id: string
  name: string
  email: string
  role: string
  phone?: string
  photoUrl?: string
  approved: boolean
  /** Effective permission strings — drives usePermissions / PermissionGuard. */
  permissions: string[]
}

class AuthService {
  static async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await api.post('/auth/login', data)
    return response.data
  }

  static async register(data: Record<string, unknown>): Promise<unknown> {
    const response = await api.post('/auth/register', data)
    return response.data
  }

  static async getMe(): Promise<User> {
    const response = await api.get('/auth/me')
    // Transform snake_case to camelCase
    const user = response.data
    return {
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      phone: user.phone,
      photoUrl: user.photo_url,
      approved: user.approved,
      permissions: user.permissions ?? [],
    }
  }

  static async updateProfile(data: Partial<{ name: string; email: string; phone: string; photo_url: string }>): Promise<User> {
    const response = await api.put('/auth/profile', data)
    const user = response.data
    return {
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      phone: user.phone,
      photoUrl: user.photo_url,
      approved: user.approved,
      permissions: user.permissions ?? [],
    }
  }

  /**
   * Tell the backend to clear the httpOnly refresh cookie. The endpoint is
   * idempotent (returns 204 even if no cookie is present), so it's safe to
   * call on every logout path including the admin-rejection branch in
   * AuthContext. The browser detaches the cookie automatically because the
   * axios instance is built with `withCredentials: true`.
   */
  static async logout(): Promise<void> {
    await api.post('/auth/logout')
  }
}

export default AuthService
export { AuthService }
export type { LoginRequest, LoginResponse, User }
